import { UseMutationOptions, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import {
  connect,
  ConnectType,
  getToken,
  ConnectError,
  disconnect,
  applicationJourneys,
  journeys,
  statistics,
  companyTravellerAttributes,
  journeyTravellerAttributes,
  users,
  list,
  AddToJourneyType,
  addToJourney,
  removeFromJourney,
} from './api';

const tokenQueryKey = () => ['journeys-token'];

export const useInvalidateJourneys = () => {
  const queryClient = useQueryClient();
  const invalidateToken = () => queryClient.invalidateQueries(tokenQueryKey());
  const invalidateApplicant = (applicationId: number) =>
    queryClient.invalidateQueries(getApplicationJourneyQueryKey(applicationId));
  return { invalidateToken, invalidateApplicant };
};

export const useToken = () => {
  return useQuery({
    queryKey: tokenQueryKey(),
    queryFn: getToken,
  });
};

export const useIsJourneysEnabled = () => {
  const tokenQuery = useToken();
  return tokenQuery.data?.ok;
};

export const useConnect = (options?: UseMutationOptions<unknown, unknown, ConnectType>) => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess, showError } = useToaster();
  const { invalidateToken } = useInvalidateJourneys();

  return useMutation<unknown, ConnectError, ConnectType>({
    ...options,
    mutationFn: connect,
    onSuccess: (...data) => {
      showSuccess(t('journeysConnected'));
      invalidateToken();
      options?.onSuccess?.(...data);
    },
    onError: (data, ...rest) => {
      if (data.response?.data.error) {
        showError(data.response.data.error);
      } else {
        showError(t('journeysNotConnected'));
      }
      options?.onError?.(data, ...rest);
    },
  });
};

export const useDisconnect = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess, showError } = useToaster();
  const { invalidateToken } = useInvalidateJourneys();

  return useMutation({
    mutationFn: disconnect,
    onSuccess: (...data) => {
      showSuccess(t('journeysDisconnected'));
      invalidateToken();
    },
    onError: (data, ...rest) => {
      showError(t('journeysNotDisconnected'));
    },
  });
};

const getApplicationJourneyQueryKey = (applicationId: number) => [
  'journeys-application',
  applicationId,
];

export const useApplicationJourneys = (applicationId: number) => {
  return useQuery({
    queryKey: getApplicationJourneyQueryKey(applicationId),
    queryFn: () => applicationJourneys(applicationId),
  });
};

const journeysQueryKey = ['journeys-list'];
export const useJourneys = () => {
  return useQuery({
    queryKey: journeysQueryKey,
    queryFn: () => journeys(),
  });
};

const journeysStatsQueryKey = ['journeys-stats'];
export const useJourneysStats = () => {
  return useQuery({
    queryKey: journeysStatsQueryKey,
    queryFn: () => statistics(),
  });
};

const companyTravellerAttributesQueryKey = ['company-traveller-attributes'];
export const useCompanyTravellerAttributes = () => {
  return useQuery({
    queryKey: companyTravellerAttributesQueryKey,
    queryFn: () => companyTravellerAttributes(),
  });
};

const getJourneyTravellerAttributesQueryKey = (journeyId: string) => [
  'journey-traveller-attributes',
  journeyId,
];

export const useJourneyTravllerAttributes = (journeyId: string) => {
  return useQuery({
    queryKey: getJourneyTravellerAttributesQueryKey(journeyId),
    queryFn: () => journeyTravellerAttributes(journeyId),
  });
};

const usersQueryKey = ['journeys-users'];
export const useUsers = () => {
  return useQuery({
    queryKey: usersQueryKey,
    queryFn: () => users(),
  });
};

const getListQueryKey = (listId: string) => ['journey-list', listId];

export const useList = (listId: string) => {
  return useQuery({
    queryKey: getListQueryKey(listId),
    queryFn: () => list(listId),
    enabled: Boolean(listId),
  });
};

export const useAddToJourney = (
  options?: UseMutationOptions<unknown, unknown, AddToJourneyType>
) => {
  const { t } = useScopedTranslate(locale);
  const { showError } = useToaster();
  const { invalidateApplicant } = useInvalidateJourneys();
  return useMutation({
    ...options,
    mutationFn: (data) => addToJourney(data),
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);
      if (variables.applications.length === 1) {
        invalidateApplicant(variables.applications[0]);
      }
    },
    onError: () => {
      showError(t('travellersNotAdded'));
    },
  });
};

export const useRemoveFromJourneys = (applicationId: number, travellerJourneyId: string) => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess, showError } = useToaster();
  const { invalidateApplicant } = useInvalidateJourneys();
  return useMutation({
    mutationFn: () => removeFromJourney(applicationId, travellerJourneyId),
    onSuccess: () => {
      invalidateApplicant(applicationId);
      showSuccess(t('journeyDeleted'));
    },
    onError: () => {
      showError(t('journeyNotDeleted'));
    },
  });
};

const locale = {
  en: {
    journeysConnected: 'Journeys connected',
    journeysNotConnected: 'Journeys not connected',
    journeysDisconnected: 'Journeys has been removed',
    journeysNotDisconnected: 'Journeys could not been removed',
    travellersNotAdded: 'Travellers could not been added',
    journeyDeleted: 'Journey has been deleted',
    journeyNotDeleted: 'Journey could not be deleted',
  },
  is: {
    journeysConnected: 'Journeys tengt',
    journeysNotConnected: 'Journeys ekki tengt',
    journeysDisconnected: 'Journeys hefur verið aftengt',
    journeysNotDisconnected: 'Journeys var ekki hægt að aftengja',
    travellersNotAdded: 'Ekki var hægt að bæta við Travellers',
    journeyDeleted: 'Journey hefur verið eytt',
    journeyNotDeleted: 'Journey var ekki hægt að eyða',
  },
};
